import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { PICKUP_LOCK_MINUTES, PRESENCE_RADIUS_M } from '@/lib/pickupPoint'
import { setPlatformRates } from '@/lib/platformRates'

interface PlatformSetting {
  key: string
  value: string
  description: string
  data_type: 'numeric' | 'integer' | 'boolean' | 'text'
  updated_at: string
}

export function usePlatformSettings() {
  return useQuery({
    queryKey: ['platform_settings'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_all_platform_settings')
      if (error) throw error
      return (data ?? []) as PlatformSetting[]
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function usePlatformSetting(key: string) {
  return useQuery({
    queryKey: ['platform_setting', key],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_platform_setting', { p_key: key })
      if (error) throw error
      const row = Array.isArray(data) ? data[0] : data
      return row ?? null
    },
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Helper: retorna true se maintenance_mode = 'true' nos platform_settings.
 * Útil para verificações rápidas sem depender de dois hooks separados.
 */
export function useMaintenanceMode(): boolean {
  const { data } = usePlatformSetting('maintenance_mode')
  return data?.value === 'true'
}

/**
 * Regras do ponto de encontro (2026-10-15), lidas de platform_settings para
 * as telas acompanharem o que o banco confere. Enquanto carrega (ou se a
 * leitura falhar), usa os valores padrão.
 */
export function usePickupRules(): { radiusM: number; lockMinutes: number } {
  const { data: radius } = usePlatformSetting('presence_radius_m')
  const { data: lock } = usePlatformSetting('pickup_change_lock_minutes')
  const r = Number(radius?.value)
  const l = Number(lock?.value)
  return {
    radiusM: Number.isFinite(r) && r > 0 ? r : PRESENCE_RADIUS_M,
    lockMinutes: Number.isFinite(l) && l >= 0 ? l : PICKUP_LOCK_MINUTES,
  }
}

/**
 * Lê a taxa da plataforma e o desconto Premium de platform_settings e os
 * publica em `lib/platformRates`, para os textos e simulações acompanharem a
 * cobrança. Montado uma vez no App; não renderiza nada.
 */
export function PlatformRatesSync(): null {
  const { data: fee } = usePlatformSetting('platform_fee_percent')
  const { data: premium } = usePlatformSetting('premium_discount_percent')
  useEffect(() => {
    setPlatformRates({
      feePercent: fee?.value != null ? Number(fee.value) : undefined,
      premiumDiscountPercent: premium?.value != null ? Number(premium.value) : undefined,
    })
  }, [fee?.value, premium?.value])
  return null
}
